import { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import MatchFilterSheet from './MatchFilterSheet';

export const MATCH_VIEW_TABS = [
  { label: 'All', to: '/matches' },
  { label: 'Saved', to: '/saved-matches' },
  { label: 'Hidden', to: '/hidden-matches' },
];

/**
 * Shared mobile compact bar (All / Saved / Hidden toggle + Filter) and desktop
 * expanded header slot for Matches, SavedMatches, and HiddenMatches.
 */
export default function MatchesMobileChromeHeader({ activePath, filters, children }) {
  const {
    openFilterSheet,
    activeFilterCount,
    isFilterSheetOpen,
    closeFilterSheet,
  } = filters;

  const [showMobileChromeBar, setShowMobileChromeBar] = useState(false);
  const [mobileChromeBarHeight, setMobileChromeBarHeight] = useState(0);
  const mobileChromeInflowRef = useRef(null);
  const mobileChromeSlotRef = useRef(null);
  const mobileChromeBarHeightRef = useRef(0);
  const navbarHeightRef = useRef(125);
  const lastScrollYRef = useRef(typeof window !== 'undefined' ? window.scrollY : 0);
  const mobileChromeHandedOffRef = useRef(false);

  useLayoutEffect(() => {
    const readNavbarHeight = () => {
      const v = getComputedStyle(document.documentElement).getPropertyValue('--navbar-height').trim();
      navbarHeightRef.current = v ? parseFloat(v) : 125;
    };
    readNavbarHeight();
    window.addEventListener('resize', readNavbarHeight);
    return () => window.removeEventListener('resize', readNavbarHeight);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        const lastY = lastScrollYRef.current;
        const delta = y - lastY;
        const chromeSlotBottom = navbarHeightRef.current + mobileChromeBarHeightRef.current;
        const pastChromeSlot = y > chromeSlotBottom;
        const slotTop = mobileChromeSlotRef.current?.getBoundingClientRect().top ?? -1;

        if (y > chromeSlotBottom + 30) {
          mobileChromeHandedOffRef.current = false;
        }

        if (slotTop >= 0 || y <= 10) {
          setShowMobileChromeBar(false);
          if (slotTop >= 0) mobileChromeHandedOffRef.current = true;
        } else if (delta > 5) {
          setShowMobileChromeBar(false);
        } else if (delta < -5 && pastChromeSlot && !mobileChromeHandedOffRef.current) {
          setShowMobileChromeBar(true);
        }
        lastScrollYRef.current = y;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useLayoutEffect(() => {
    const el = mobileChromeInflowRef.current;
    if (!el) return;
    const measure = () => {
      const h = el.offsetHeight;
      if (h > 0) {
        mobileChromeBarHeightRef.current = h;
        setMobileChromeBarHeight(h);
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [activeFilterCount, activePath]);

  useEffect(() => {
    const handleAddOpened = () => {
      if (isFilterSheetOpen) closeFilterSheet();
    };
    window.addEventListener('bottomTabBarAddOpened', handleAddOpened);
    return () => window.removeEventListener('bottomTabBarAddOpened', handleAddOpened);
  }, [isFilterSheetOpen, closeFilterSheet]);

  const renderMobileChromeBar = () => (
    <div className="matches-compact-bar-inner">
      <div
        className="matches-view-toggle"
        role="tablist"
        aria-label="Match view"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          backgroundColor: 'white',
          borderRadius: '9999px',
          padding: '4px',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
          border: '1px solid #e5e7eb',
          flex: 1,
          minWidth: 0,
          maxWidth: 'calc(100% - 104px)',
          boxSizing: 'border-box',
        }}
      >
        {MATCH_VIEW_TABS.map(({ label, to }) => {
          const isActive = activePath === to;
          return (
            <Link
              key={to}
              to={to}
              role="tab"
              aria-selected={isActive}
              className="matches-view-toggle-btn"
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0.6rem 0.35rem',
                borderRadius: '9999px',
                border: 'none',
                fontWeight: 700,
                fontSize: '0.875rem',
                lineHeight: 1.1,
                letterSpacing: '-0.02em',
                fontFamily: 'Nunito, sans-serif',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                textDecoration: 'none',
                textAlign: 'center',
                transition: 'background-color 0.2s, color 0.2s',
                backgroundColor: isActive ? '#C89F3C' : 'transparent',
                color: isActive ? 'white' : '#6B7280',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                boxSizing: 'border-box',
              }}
            >
              {label}
            </Link>
          );
        })}
      </div>
      <button
        type="button"
        className="matches-compact-filter-btn"
        onClick={openFilterSheet}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          padding: '0.6rem 1rem',
          borderRadius: '9999px',
          border: '1px solid #C89F3C',
          backgroundColor: activeFilterCount > 0 ? '#C89F3C' : 'white',
          color: activeFilterCount > 0 ? 'white' : '#374151',
          fontFamily: 'Nunito, sans-serif',
          fontWeight: 700,
          fontSize: '0.875rem',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
        aria-label="Open filters"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="10" y1="18" x2="14" y2="18"/>
        </svg>
        Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
      </button>
    </div>
  );

  return (
    <>
      <div className="matches-sticky-header">
        <style>{`
          .matches-compact-bar-inner {
            max-width: 1100px;
            margin: 0 auto;
            width: 100%;
            box-sizing: border-box;
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 8px;
          }
          .matches-sticky-header .matches-header-expanded {
            display: block;
          }
          .matches-sticky-header .matches-mobile-chrome-slot {
            display: none;
          }
          .matches-sticky-header .matches-compact-bar-mobile-fixed {
            display: none;
          }
          @media (min-width: 769px) {
            .matches-sticky-header .matches-compact-bar {
              display: none !important;
            }
            .matches-sticky-header .matches-desktop-filter-bar {
              display: block;
            }
            .matches-sticky-header .matches-header-expanded {
              text-align: left !important;
              padding-left: 0 !important;
              padding-right: 0 !important;
            }
            .matches-sticky-header .matches-header-back-link {
              max-width: 1100px;
              width: 100%;
              margin-left: auto;
              margin-right: auto;
              padding-left: 2rem;
              padding-right: 2rem;
              box-sizing: border-box;
              display: flex !important;
              align-items: center;
            }
            .matches-sticky-header .matches-header-expanded-inner {
              max-width: 1100px;
              width: 100%;
              margin-left: auto;
              margin-right: auto;
              padding-left: 2rem;
              padding-right: 2rem;
              box-sizing: border-box;
              display: flex;
              flex-wrap: wrap;
              align-items: flex-start;
              gap: 0.5rem 5rem;
            }
            .matches-sticky-header .matches-header-copy {
              flex: 0 1 auto;
              min-width: 0;
            }
            .matches-sticky-header .matches-header-links {
              flex: 0 0 auto;
              margin-left: auto;
              margin-right: 1rem;
              display: flex;
              flex-direction: column;
              align-items: flex-end;
              gap: 0.5rem;
              align-self: flex-end;
            }
            .matches-sticky-header .matches-header-expanded h1 {
              text-align: left !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            .matches-sticky-header .matches-header-description {
              margin: 0 !important;
              padding: 0 !important;
              max-width: none !important;
              text-align: left !important;
            }
            @media (max-width: 900px) {
              .matches-sticky-header .matches-header-expanded-inner {
                flex-direction: column;
                gap: 1rem;
              }
              .matches-sticky-header .matches-header-links {
                margin-left: 0;
                margin-right: 0;
                align-self: stretch;
                align-items: flex-end;
              }
            }
          }
          @media (max-width: 768px) {
            .matches-page-content {
              padding-top: 0 !important;
            }
            .matches-sticky-header .matches-header-expanded {
              display: none !important;
            }
            .matches-sticky-header .matches-mobile-chrome-slot {
              display: block !important;
              position: relative;
              flex-shrink: 0;
              background-color: #F7F3EB;
              overflow: hidden;
            }
            .matches-sticky-header .matches-compact-bar-inflow {
              display: block;
              padding: 1rem 1rem 1.5rem 1rem;
              background-color: #F7F3EB;
              box-sizing: border-box;
            }
            .matches-sticky-header .matches-compact-bar-inflow.is-suppressed {
              visibility: hidden;
              pointer-events: none;
            }
            .matches-sticky-header .matches-compact-bar-mobile-fixed {
              display: block;
            }
            .matches-sticky-header .matches-compact-bar {
              display: none;
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              z-index: 100;
              padding: 0.35rem 1rem 0.85rem 1rem;
              background-color: #F7F3EB;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
              box-sizing: border-box;
              pointer-events: none;
              transform: translateY(-100%);
              transition: transform 0.2s ease;
            }
            .matches-sticky-header .matches-compact-bar.is-mobile-visible {
              display: block;
              transform: translateY(0);
              pointer-events: auto;
            }
            .matches-sticky-header .matches-compact-filter-btn {
              padding: 0.6rem 1rem !important;
              font-size: 0.875rem !important;
            }
            .matches-sticky-header .matches-view-toggle-btn {
              padding: 0.6rem 0.35rem !important;
              font-size: 0.875rem !important;
              line-height: 1.1 !important;
              letter-spacing: -0.02em !important;
            }
          }
        `}</style>

        <div
          ref={mobileChromeSlotRef}
          className="matches-mobile-chrome-slot"
          style={mobileChromeBarHeight > 0 ? { height: mobileChromeBarHeight, minHeight: mobileChromeBarHeight } : undefined}
        >
          <div
            ref={mobileChromeInflowRef}
            className={['matches-compact-bar-inflow', showMobileChromeBar && 'is-suppressed'].filter(Boolean).join(' ')}
          >
            {renderMobileChromeBar()}
          </div>
        </div>

        <div
          className={[
            'matches-compact-bar',
            showMobileChromeBar && 'is-mobile-visible',
          ].filter(Boolean).join(' ')}
        >
          <div className="matches-compact-bar-mobile-fixed">
            {renderMobileChromeBar()}
          </div>
        </div>

        <div className="matches-header-expanded" style={{ padding: '0 1rem' }}>
          {children}
        </div>
      </div>

      <MatchFilterSheet filters={filters} />
    </>
  );
}
